import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { PostSubmissionBehavior } from '@/types/artifact';

/** Submission state for a single form */
export interface FormSubmissionState {
  status: 'idle' | 'submitting' | 'success' | 'error';
  error?: string;
  result?: unknown;
  /** The interpolated message that was sent (for llm method) */
  submittedMessage?: string;
  /** The raw form values at submission time */
  submittedValues?: Record<string, string | number | boolean>;
  /** Field labels for display purposes */
  fieldLabels?: Record<string, string>;
}

/** Per-component post-submission state */
export interface ComponentPostSubmissionState {
  behavior: PostSubmissionBehavior;
  replacementContent?: string;
}

/** Mutable UI state for a single artifact instance */
export interface ArtifactUIState {
  formValues: Record<string, string | number | boolean>;
  formSubmissions: Record<string, FormSubmissionState>;
  componentStates: Record<string, ComponentPostSubmissionState>;
  displayMode: 'inline' | 'sidebar' | 'modal' | 'fullscreen';
  isExpanded: boolean;
}

export interface ArtifactState {
  /** artifactsByConversation[convId][artifactId] = ArtifactUIState */
  artifactsByConversation: Record<string, Record<string, ArtifactUIState>>;
  /** Currently open sidebar artifact id */
  sidebarArtifactId: string | null;
  /** Currently open modal artifact id */
  modalArtifactId: string | null;
  /** Active conversation for artifact state */
  activeConversationId: string | null;
}

const initialState: ArtifactState = {
  artifactsByConversation: {},
  sidebarArtifactId: null,
  modalArtifactId: null,
  activeConversationId: null,
};

function getOrCreateArtifact(
  state: ArtifactState,
  conversationId: string,
  artifactId: string,
): ArtifactUIState {
  const convArtifacts = (state.artifactsByConversation[conversationId] ??= {});
  return (convArtifacts[artifactId] ??= {
    formValues: {},
    formSubmissions: {},
    componentStates: {},
    displayMode: 'inline',
    isExpanded: false,
  });
}

const artifactSlice = createSlice({
  name: 'artifact',
  initialState,
  reducers: {
    setActiveConversation(state, action: PayloadAction<string>) {
      state.activeConversationId = action.payload;
    },

    initArtifact(state, action: PayloadAction<{ conversationId: string; artifactId: string }>) {
      getOrCreateArtifact(state, action.payload.conversationId, action.payload.artifactId);
    },

    setFormValue(
      state,
      action: PayloadAction<{
        conversationId: string;
        artifactId: string;
        fieldName: string;
        value: string | number | boolean;
      }>,
    ) {
      const { conversationId, artifactId, fieldName, value } = action.payload;
      const artifact = getOrCreateArtifact(state, conversationId, artifactId);
      artifact.formValues[fieldName] = value;
    },

    setFormSubmissionState(
      state,
      action: PayloadAction<{
        conversationId: string;
        artifactId: string;
        formId: string;
        submission: FormSubmissionState;
      }>,
    ) {
      const { conversationId, artifactId, formId, submission } = action.payload;
      const artifact = getOrCreateArtifact(state, conversationId, artifactId);
      artifact.formSubmissions[formId] = submission;
    },

    setComponentPostSubmission(
      state,
      action: PayloadAction<{
        conversationId: string;
        artifactId: string;
        componentId: string;
        postState: ComponentPostSubmissionState;
      }>,
    ) {
      const { conversationId, artifactId, componentId, postState } = action.payload;
      const artifact = getOrCreateArtifact(state, conversationId, artifactId);
      artifact.componentStates[componentId] = postState;
    },

    openInSidebar(state, action: PayloadAction<{ artifactId: string }>) {
      state.sidebarArtifactId = action.payload.artifactId;
      state.modalArtifactId = null;
    },

    closeSidebar(state) {
      state.sidebarArtifactId = null;
    },

    openInModal(state, action: PayloadAction<{ artifactId: string }>) {
      state.modalArtifactId = action.payload.artifactId;
      state.sidebarArtifactId = null;
    },

    closeModal(state) {
      state.modalArtifactId = null;
    },

    clearConversationArtifacts(state, action: PayloadAction<string>) {
      state.artifactsByConversation[action.payload] = {};
    },
  },
});

export const {
  setActiveConversation: setArtifactActiveConversation,
  initArtifact,
  setFormValue,
  setFormSubmissionState,
  setComponentPostSubmission,
  openInSidebar,
  closeSidebar,
  openInModal,
  closeModal,
  clearConversationArtifacts,
} = artifactSlice.actions;

// Selectors
export const selectSidebarArtifactId = (state: { artifact: ArtifactState }): string | null =>
  state.artifact.sidebarArtifactId;

export const selectModalArtifactId = (state: { artifact: ArtifactState }): string | null =>
  state.artifact.modalArtifactId;

export const selectArtifactState = (
  state: { artifact: ArtifactState },
  conversationId: string,
  artifactId: string,
): ArtifactUIState | undefined =>
  state.artifact.artifactsByConversation[conversationId]?.[artifactId];

export default artifactSlice.reducer;
